import React, { Fragment } from "react";
import * as R from "ramda";
import { compose, withState, withProps, defaultProps } from "recompose";
import { Input, Radio, Checkbox } from "antd";
import { withTranslation } from "react-i18next";

import { search } from "../../utils/strings";

import NonResults from "./filters/NonResults";

import styled from "styled-components/macro";

const Container = styled.div`
  display: flex;
  margin-bottom: 24px;
  flex-direction: row-reverse;
  justify-content: ${({ onlySearch }) =>
    onlySearch ? "flex-end" : "space-between"};
  align-items: center;
  padding: ${({ onlySearch }) => !onlySearch && "5px 10px"};
  box-shadow: ${({ onlySearch }) => !onlySearch && "0px 1px 3px #00000026"};
  background: ${({ onlySearch }) => !onlySearch && "white"};

  .radio {
    display: flex;
    flex-direction: row;
    align-items: center;
  }

  .search {
    max-width: 190px;
    height: 35px;
  }

  .search,
  .multiple {
    width: fit-content;
    margin-right: 16px;
    min-width: 120px;
  }

  .filter-group {
    display: flex;
    margin-right: 20px;
    align-items: center;
  }
`;

const radioPropFilter = (value, prop) =>
  R.unless(R.always(R.equals(value, "all")), R.filter(R.propEq(prop, value)));

const multiplePropFilter = (value, prop) =>
  R.filter(
    R.pipe(
      R.prop(prop),
      String,
      R.includes(R.__, value)
    )
  );

export const searchPropFilter = (value, prop) =>
  R.filter(candidate => search(value, R.prop(prop, candidate)));

const getFilterQuantity = R.curry((prop, filter, data, value) =>
  R.pipe(
    filter(value, prop),
    R.length
  )(data)
);

const FilterContent = styled.div`
  padding: 10px;
  padding-right: 0px;

  .ant-radio-wrapper {
    font-size: 12px;
    line-height: 16px;
    margin-right: 10px;
    padding: 5px 8px !important;
    border-right: ${({ theme }) => `1px solid ${theme["@gray-40"]}`};
    display: flex;
    align-items: center;
    width: 130px;
    color: ${({ theme }) => theme["@gray-60"]};
    padding-right: 15px;

    > span {
      &:last-child {
        white-space: break-spaces;
        text-align: left;
        margin-left: 5px;
      }
    }

    &:hover {
      color: ${({ theme }) => theme["@primary"]} !important;
    }

    &:last-child {
      margin-right: 0px;
    }
  }

  .ant-radio-group-item {
    padding: 5px 8px !important;

    &:hover {
      color: ${({ theme }) => theme["@primary"]} !important;
      border-color: ${({ theme }) => theme["@primary"]} !important;
    }
  }

  .ant-checkbox-wrapper,
  .ant-checkbox-group-item {
    font-size: 12px;
    line-height: 16px;
    display: flex;
    align-items: center;
    padding: 5px 8px !important;
    border: ${({ theme }) => `1px solid ${theme["@gray-40"]}`} !important;
    border-radius: 20px !important;
    color: ${({ theme }) => theme["@gray-60"]};
    &:hover {
      color: ${({ theme }) => theme["@primary"]} !important;
      border-color: ${({ theme }) => theme["@primary"]} !important;
    }
  }

  .ant-radio-wrapper-checked {
    color: ${({ theme }) => theme["@primary"]} !important;
    font-weight: bold;
  }
  .ant-checkbox-wrapper-checked {
    border: ${({ theme }) => `1px solid ${theme["@primary"]}`} !important;
    color: ${({ theme }) => theme["@primary"]} !important;
  }

  .ant-checkbox-inner {
    border-radius: 50%;
  }
`;

const Filters = ({
  t,
  data,
  children,
  radioValue,
  radioOptions,
  onRadioChange,
  setRadioValue,
  showRadio,
  multipleValues,
  multipleOptions,
  onMultipleChange,
  setMultipleValues,
  showMultiple,
  searchValue,
  onSearchChange,
  setSearchValue,
  showSearch,
  isEmpty,
  noResultsTitle = t("opportunity:noResults.emptyResults"),
  noResultsMessages = [
    showSearch && t("opportunity:noResults.orthography"),
    showMultiple && showRadio && t("opportunity:noResults.filters")
  ]
}) => {
  return (
    <Fragment>
      <Container onlySearch={!showMultiple && !showRadio} className="filters">
        {showSearch && (
          <Input.Search
            data-test="filters-search"
            className="search"
            defaultValue={searchValue}
            placeholder={t("global:search")}
            onChange={e => {
              const value = e.target.value;

              setSearchValue(value);
              onSearchChange && onSearchChange(value);
            }}
          />
        )}
        {(showMultiple || showRadio) && (
          <div style={{ display: "flex" }}>
            {showRadio && (
              <div className="filter-group">
                <FilterContent>
                  <Radio.Group
                    className="radio"
                    defaultValue={radioValue}
                    onChange={event => {
                      setRadioValue(event.target.value);
                      onRadioChange && onRadioChange(event.target.value);
                    }}
                  >
                    {radioOptions.map(({ value, label }) => (
                      <Radio
                        data-test={`filter-${value}`}
                        key={value}
                        value={value}
                      >
                        {label}
                      </Radio>
                    ))}
                  </Radio.Group>
                </FilterContent>
              </div>
            )}
            {multipleOptions && multipleOptions.length > 0 && (
              <div className="filter-group">
                {multipleOptions.map(({ value, label }) => {
                  const enabled = multipleValues.includes(value);
                  return (
                    <div
                      key={label}
                      className="select-option"
                      data-test={`filter-${value}`}
                    >
                      <FilterContent>
                        <Checkbox
                          checked={enabled}
                          onChange={() => {
                            const newValues = enabled
                              ? multipleValues.filter(val => val !== value)
                              : [...multipleValues, value];
                            setMultipleValues(newValues);
                            onMultipleChange && onMultipleChange(newValues);
                          }}
                        >
                          {label}
                        </Checkbox>
                      </FilterContent>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </Container>
      {children && !isEmpty && data.length > 0 ? (
        children({ data })
      ) : isEmpty && children ? (
        children({ data: [] })
      ) : (
        <NonResults
          title={noResultsTitle}
          messages={noResultsMessages.filter(Boolean)}
        />
      )}
    </Fragment>
  );
};

const enhancer = compose(
  withTranslation(["global"]),
  withState(
    "searchValue",
    "setSearchValue",
    R.propOr("", "defaultSearchValue")
  ),
  withState(
    "multipleValues",
    "setMultipleValues",
    R.propOr([], "defaultMultipleValues")
  ),
  defaultProps({
    searchSelector: searchPropFilter,
    multipleSelector: multiplePropFilter,
    radioSelector: radioPropFilter
  }),
  withState("radioValue", "setRadioValue", R.propOr(null, "defaultRadioValue")),
  withProps(
    ({ searchProp, searchSelector, multipleOptions, radioOptions }) => ({
      showMultiple: !!multipleOptions,
      showRadio: !!radioOptions,
      showSearch: !!searchProp || !!searchSelector
    })
  ),
  withProps(
    ({
      data,
      showRadio,
      radioProp,
      radioValue,
      radioSelector,
      showMultiple,
      multipleProp,
      multipleValues,
      multipleSelector,
      multipleOptions,
      searchProp,
      showSearch,
      searchValue,
      searchSelector
    }) => ({
      isEmpty: data.length === 0,
      data: R.pipe(
        // Filter per search value just when search is visible
        R.when(R.always(showSearch), searchSelector(searchValue, searchProp)),
        // Filter per multiple values just when multiple is visible
        R.when(
          R.always(showMultiple),
          multipleSelector(multipleValues, multipleProp)
        ),
        // Filter per radio value just when radio is visible
        R.when(R.always(showRadio), radioSelector(radioValue, radioProp))
      )(data),
      multipleOptions: R.pipe(
        R.defaultTo([]),
        R.map(({ value, ...rest }) =>
          R.assoc(
            "quantity",
            getFilterQuantity(multipleProp, multipleSelector, data, value),
            { value, ...rest }
          )
        )
      )(multipleOptions)
    })
  )
);

export default enhancer(Filters);
